/**
 * CanvasBlockRenderer — renders typed deliverable blocks (UI-1).
 *
 * Each block type has its own minimal but functional component. The agent
 * emits the structured payload in JSON; this layer turns that into
 * affordances (sortable tables, weighted decision matrices, citation
 * chips, simple bar charts). Markdown blocks delegate to
 * MarkdownRenderer so existing styling continues to apply.
 */
import { useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import { ArrowDown, ArrowUp, ChevronsUpDown, ExternalLink, Quote } from "@/components/icons"
import { MarkdownRenderer } from "@/components/markdown-renderer"
import { STATUS_TONES } from "@/components/ui/tone-classes"
import { cn } from "@/lib/utils"

import {
  type CanvasBlock,
  type ChartBlock,
  type ClaimWithCitationBlock,
  type ComparisonTableBlock,
  type DecisionMatrixBlock,
  type ExecutiveSummaryBlock,
  type QuoteCardBlock,
  type SourcePanelBlock,
  type TimelineBlock,
} from "./canvas-blocks"

interface CanvasBlockRendererProps {
  block: CanvasBlock
  onCitationClick?: (citation: string) => void
}

export function CanvasBlockRenderer({ block, onCitationClick }: CanvasBlockRendererProps) {
  switch (block.type) {
    case "executive_summary":
      return <ExecutiveSummaryView block={block} />
    case "claim_with_citation":
      return <ClaimWithCitationView block={block} onCitationClick={onCitationClick} />
    case "comparison_table":
      return <ComparisonTableView block={block} onCitationClick={onCitationClick} />
    case "decision_matrix":
      return <DecisionMatrixView block={block} />
    case "quote_card":
      return <QuoteCardView block={block} />
    case "timeline":
      return <TimelineView block={block} />
    case "chart":
      return <ChartView block={block} />
    case "source_panel":
      return <SourcePanelView block={block} />
    case "markdown":
      return (
        <div className="canvas-block-markdown">
          <MarkdownRenderer className="prose-sm max-w-none" content={block.content} />
        </div>
      )
    default: {
      // Exhaustiveness check — if a new block type lands without a renderer,
      // this stays compilable but signals at runtime via the empty fallback.
      const _exhaustive: never = block
      return _exhaustive
    }
  }
}

function ExecutiveSummaryView({ block }: { block: ExecutiveSummaryBlock }) {
  const { t } = useTranslation()
  const [collapsed, setCollapsed] = useState(false)
  return (
    <section
      className="border-primary/40 bg-primary/5 relative my-3 rounded-md border-l-4 px-4 py-3"
      aria-label={t("deepExplorer.canvas.executive_summary_aria", {
        defaultValue: "Executive summary",
      })}
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-foreground text-sm font-semibold">
          {block.title ?? t("deepExplorer.canvas.tldr", { defaultValue: "TL;DR" })}
        </h3>
        <button
          className="text-muted-foreground hover:text-foreground text-[11px]"
          type="button"
          onClick={() => setCollapsed((v) => !v)}
        >
          {collapsed
            ? t("deepExplorer.canvas.expand", { defaultValue: "Expand" })
            : t("deepExplorer.canvas.collapse", { defaultValue: "Collapse" })}
        </button>
      </div>
      {!collapsed && (
        <>
          <p className="text-foreground mt-1.5 text-sm leading-relaxed">{block.body}</p>
          {block.highlights && block.highlights.length > 0 && (
            <ul className="text-muted-foreground mt-2 list-disc pl-5 text-sm">
              {block.highlights.map((h, i) => (
                <li key={i}>{h}</li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  )
}

function confidenceClassName(confidence: string | undefined): string {
  if (confidence === "high") return STATUS_TONES.success.soft
  if (confidence === "low") return "bg-warning/30 text-warning-ink "
  return "bg-muted text-muted-foreground"
}

function ClaimWithCitationView({
  block,
  onCitationClick,
}: {
  block: ClaimWithCitationBlock
  onCitationClick?: (citation: string) => void
}) {
  const { t } = useTranslation()
  const confidenceClass = confidenceClassName(block.confidence)
  return (
    <p className="my-2 text-sm leading-relaxed">
      <span>{block.claim}</span>
      {block.confidence && (
        <span
          className={cn(
            "ml-1.5 inline-flex items-center rounded px-1.5 py-px text-[10px] font-medium tracking-wide uppercase",
            confidenceClass
          )}
        >
          {t(`deepExplorer.canvas.confidence_${block.confidence}`, {
            defaultValue: block.confidence,
          })}
        </span>
      )}
      {block.citations.map((c, i) => (
        <button
          key={i}
          className="text-primary hover:bg-primary/10 ml-1 inline-flex items-center rounded px-1 py-0.5 align-text-bottom text-[11px] font-medium"
          type="button"
          aria-label={t("deepExplorer.canvas.open_citation", {
            citation: c,
            defaultValue: `Open citation ${c}`,
          })}
          onClick={() => onCitationClick?.(c)}
        >
          [{i + 1}]
        </button>
      ))}
    </p>
  )
}

function SortIcon({ isSorted, direction }: { isSorted: boolean; direction: "asc" | "desc" }) {
  if (!isSorted) return <ChevronsUpDown className="size-3 opacity-40" />
  if (direction === "asc") return <ArrowUp className="size-3" />
  return <ArrowDown className="size-3" />
}

function ComparisonTableView({
  block,
  onCitationClick,
}: {
  block: ComparisonTableBlock
  onCitationClick?: (citation: string) => void
}) {
  const { t } = useTranslation()
  const [sort, setSort] = useState<{ col: number; dir: "asc" | "desc" } | null>(null)
  const sortedRows = useMemo(() => {
    if (!sort) return block.rows
    const idx = sort.col
    const sign = sort.dir === "asc" ? 1 : -1
    return [...block.rows].sort((a, b) => {
      const av = a[idx]
      const bv = b[idx]
      // Numeric sort if both sides parse as numbers; otherwise lexicographic.
      const aNum = typeof av === "number" ? av : Number(av)
      const bNum = typeof bv === "number" ? bv : Number(bv)
      if (Number.isFinite(aNum) && Number.isFinite(bNum)) return (aNum - bNum) * sign
      return String(av ?? "").localeCompare(String(bv ?? "")) * sign
    })
  }, [block.rows, sort])

  function toggleSort(col: number) {
    setSort((prev) => {
      if (prev?.col !== col) return { col, dir: "asc" }
      return prev.dir === "asc" ? { col, dir: "desc" } : null
    })
  }

  return (
    <section className="my-3">
      {block.title && <h4 className="text-foreground mb-1 text-sm font-semibold">{block.title}</h4>}
      <div className="border-border overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50">
              {block.columns.map((col, i) => {
                const isSorted = sort?.col === i
                return (
                  <th
                    key={i}
                    className="text-foreground px-3 py-1.5 text-left font-medium"
                    scope="col"
                  >
                    <button
                      className="hover:text-primary inline-flex items-center gap-1 focus-visible:outline-none"
                      type="button"
                      aria-label={t("deepExplorer.canvas.sort_by", {
                        column: col,
                        defaultValue: `Sort by ${col}`,
                      })}
                      onClick={() => toggleSort(i)}
                    >
                      {col}
                      <SortIcon direction={sort?.dir ?? "asc"} isSorted={isSorted} />
                    </button>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, ri) => (
              <tr key={ri} className="border-border border-t">
                {row.map((cell, ci) => {
                  const cellCitations = block.citations?.[ri]?.[ci]
                  return (
                    <td key={ci} className="text-foreground px-3 py-1.5 align-top">
                      <span>{cell == null ? "—" : String(cell)}</span>
                      {cellCitations?.map((c, k) => (
                        <button
                          key={k}
                          className="text-primary hover:bg-primary/10 ml-1 inline-flex items-center rounded px-1 text-[10px]"
                          type="button"
                          onClick={() => onCitationClick?.(c)}
                        >
                          [{k + 1}]
                        </button>
                      ))}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function DecisionMatrixView({ block }: { block: DecisionMatrixBlock }) {
  const { t } = useTranslation()
  // Editable per-render weights — start from the agent's defaults but let
  // the user re-weight inline to see how the totals shift.
  const [weights, setWeights] = useState<number[]>(() => block.criteria.map((c) => c.weight ?? 1))
  const totals = useMemo(() => {
    const totalWeight = weights.reduce((s, w) => s + (Number.isFinite(w) ? w : 0), 0) || 1
    return block.options.map((_, optIdx) => {
      let sum = 0
      for (let cIdx = 0; cIdx < block.criteria.length; cIdx++) {
        const score = block.scores[optIdx]?.[cIdx] ?? 0
        sum += score * (weights[cIdx] ?? 0)
      }
      return sum / totalWeight
    })
  }, [block.criteria.length, block.options, block.scores, weights])
  const winner = totals.indexOf(Math.max(...totals))

  return (
    <section className="my-3">
      {block.title && <h4 className="text-foreground mb-1 text-sm font-semibold">{block.title}</h4>}
      <div className="border-border overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50">
              <th className="text-foreground px-3 py-1.5 text-left font-medium">
                {t("deepExplorer.canvas.option", { defaultValue: "Option" })}
              </th>
              {block.criteria.map((c, ci) => (
                <th key={ci} className="px-3 py-1.5 text-left">
                  <div className="text-foreground font-medium">{c.name}</div>
                  <label className="text-muted-foreground mt-0.5 flex items-center gap-1 text-[10px]">
                    {t("deepExplorer.canvas.weight", { defaultValue: "weight" })}
                    <input
                      className="border-border bg-background w-12 rounded border px-1 py-0 text-[11px]"
                      max={10}
                      min={0}
                      step={0.5}
                      type="number"
                      value={weights[ci]}
                      onChange={(e) => {
                        const v = Number(e.target.value)
                        setWeights((prev) => {
                          const next = [...prev]
                          next[ci] = Number.isFinite(v) ? v : 0
                          return next
                        })
                      }}
                    />
                  </label>
                </th>
              ))}
              <th className="text-foreground px-3 py-1.5 text-right font-medium">
                {t("deepExplorer.canvas.score", { defaultValue: "Score" })}
              </th>
            </tr>
          </thead>
          <tbody>
            {block.options.map((option, oi) => (
              <tr
                key={oi}
                className={cn("border-border border-t", oi === winner && "bg-primary/5")}
              >
                <td className="text-foreground px-3 py-1.5 font-medium">{option}</td>
                {block.criteria.map((_, ci) => (
                  <td key={ci} className="text-foreground px-3 py-1.5 tabular-nums">
                    {block.scores[oi]?.[ci] ?? "—"}
                  </td>
                ))}
                <td
                  className={cn(
                    "text-foreground px-3 py-1.5 text-right font-semibold tabular-nums",
                    oi === winner && "text-primary"
                  )}
                >
                  {totals[oi].toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function QuoteCardView({ block }: { block: QuoteCardBlock }) {
  return (
    <figure className="border-primary/40 bg-muted/30 my-3 rounded-md border-l-4 px-4 py-3">
      <Quote className="text-primary/70 mb-1 size-4" />
      <blockquote className="text-foreground text-sm leading-relaxed italic">
        “{block.quote}”
      </blockquote>
      {(block.attribution || block.source) && (
        <figcaption className="text-muted-foreground mt-2 text-xs">
          {block.attribution && <span className="font-medium">{block.attribution}</span>}
          {block.attribution && block.source ? " · " : ""}
          {block.source && <span>{block.source}</span>}
        </figcaption>
      )}
    </figure>
  )
}

function TimelineView({ block }: { block: TimelineBlock }) {
  return (
    <section className="my-3">
      {block.title && <h4 className="text-foreground mb-1 text-sm font-semibold">{block.title}</h4>}
      <ol className="border-border ml-3 space-y-2 border-l pl-4">
        {block.entries.map((entry, i) => (
          <li key={i} className="relative">
            <span className="bg-primary absolute -left-[1.45rem] mt-1 size-2 rounded-full" />
            <div className="text-muted-foreground text-[11px] tracking-wide uppercase">
              {entry.date}
            </div>
            <div className="text-foreground text-sm leading-snug">{entry.text}</div>
            {entry.source && (
              <div className="text-muted-foreground text-[11px]">{entry.source}</div>
            )}
          </li>
        ))}
      </ol>
    </section>
  )
}

function ChartView({ block }: { block: ChartBlock }) {
  // Minimal bar chart — flexbox + width%. Avoids pulling in a charting lib
  // for the first slice. SVG export hooks already exist on DeliverableView.
  const max = Math.max(1, ...block.series.map((s) => s.value))
  return (
    <section className="my-3">
      {block.title && <h4 className="text-foreground mb-1 text-sm font-semibold">{block.title}</h4>}
      <ul className="space-y-1">
        {block.series.map((s, i) => {
          const pct = (s.value / max) * 100
          return (
            <li key={i} className="text-sm">
              <div className="text-foreground flex items-center justify-between gap-2">
                <span className="truncate">{s.label}</span>
                <span className="text-muted-foreground text-[11px] tabular-nums">
                  {s.value}
                  {block.unit ? ` ${block.unit}` : ""}
                </span>
              </div>
              <div className="bg-muted/50 mt-0.5 h-2 overflow-hidden rounded">
                <div aria-hidden className="bg-primary-ink h-full" style={{ width: `${pct}%` }} />
              </div>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

function SourcePanelView({ block }: { block: SourcePanelBlock }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const sorted = useMemo(
    () => [...block.sources].sort((a, b) => (b.usageCount ?? 0) - (a.usageCount ?? 0)),
    [block.sources]
  )
  return (
    <section className="my-3">
      <button
        className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-xs font-medium"
        type="button"
        onClick={() => setOpen((v) => !v)}
      >
        {open
          ? t("deepExplorer.canvas.sources_collapse", { defaultValue: "Hide sources" })
          : t("deepExplorer.canvas.sources_expand", {
              count: sorted.length,
              defaultValue: `Show all sources (${sorted.length})`,
            })}
      </button>
      {open && (
        <ul className="border-border mt-2 space-y-1 rounded-md border p-2 text-xs">
          {sorted.map((s, i) => (
            <li key={i} className="flex items-center justify-between gap-2">
              <span className="text-foreground truncate">{s.title}</span>
              <span className="text-muted-foreground shrink-0 tabular-nums">
                {s.usageCount ? `${s.usageCount}×` : ""}
                {s.confidence ? ` · ${s.confidence}` : ""}
              </span>
              {s.url && (
                <a
                  className="text-primary shrink-0"
                  href={s.url}
                  rel="noopener noreferrer"
                  target="_blank"
                  aria-label={t("deepExplorer.canvas.open_source", {
                    title: s.title,
                    defaultValue: `Open ${s.title}`,
                  })}
                >
                  <ExternalLink className="size-3" />
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
